"use client";

import { useState } from "react";
import Image from "next/image";
import type { VideoPlayerButton } from "@/generated/sb";
import { storyblokEditable } from "@storyblok/react/rsc";
import { VideoPlayerModal } from "@repo/ui-parkmobile/VideoPlayerModal";
import { Button } from "@repo/ui-parkmobile/Button";
import { assetResolver } from "@/lib/storyblok/adaptors";
import { extractVideoConfig, isWistiaShareLink } from "@/lib/utils/wistia";
import { useWistiaId } from "@/hooks/useWistiaId";

interface VideoPlayerButtonProps {
  blok: VideoPlayerButton;
}

export default function VideoPlayerButton({ blok }: VideoPlayerButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const videoId = useWistiaId(blok.videoLink);
  const videoConfig = extractVideoConfig(blok.configuration);

  const playButton = blok.playButton?.[0];

  const playButtonImage = blok.playImage ? {
    ...assetResolver(blok.playImage),
    width: blok.playImage.width,
    height: blok.playImage.height,
    focus: blok.playImage.focus,
  } : undefined;

  if (!videoId) {
    // Share links resolve asynchronously; failures are logged by the resolver.
    if (!isWistiaShareLink(blok.videoLink)) {
      console.warn("VideoPlayerButton: No valid video ID found", blok.videoLink);
    }
    return null;
  }

  const handleClick = () => {
    setIsModalOpen(true);
  };

  if (playButton) {
    return (
      <>
        <Button
          {...storyblokEditable(blok)}
          onClick={handleClick}
          variant={playButton.variant || "primary"}
          size={playButton.size || "medium"}
        >
          {playButton.label || "Play Video"}
        </Button>
        {isModalOpen && (
          <VideoPlayerModal
            videoId={videoId}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            config={videoConfig}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button
        {...storyblokEditable(blok)}
        onClick={handleClick}
        className="inline-flex items-center justify-center"
        aria-label="Play video"
      >
        {playButtonImage ? (
          <Image
            src={playButtonImage.src}
            alt={playButtonImage.alt || "Play video"}
            width={playButtonImage.width}
            height={playButtonImage.height}
            className="w-auto h-auto"
          />
        ) : (
          <svg
            width="80"
            height="80"
            viewBox="0 0 80 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-20 h-20"
          >
            <circle cx="40" cy="40" r="40" fill="white" fillOpacity="0.9" />
            <path
              d="M32 24L56 40L32 56V24Z"
              fill="currentColor"
              className="text-gray-900"
            />
          </svg>
        )}
      </button>
      {isModalOpen && (
        <VideoPlayerModal
          videoId={videoId}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          config={videoConfig}
        />
      )}
    </>
  );
}
